import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { toastr } from '~/helpers/Toastr';
import * as Hub from '~/ogm-hub';
import * as CustomerSettingsSlice from '~/Slices/CustomerSettingsSlice';
import * as ProductDetailsSlice from '~/Slices/ProductDetailsSlice';
import { fetchBlueprintsAndRestrictionListsProductIsOn } from '~/Slices/ProductDetailsSlice';
import * as ProductHeaderPricesSlice from '~/Slices/ProductHeaderPricesSlice';
import * as ProductHeadersSlice from '~/Slices/ProductHeadersSlice';
import * as ProductNutritionalInformationSlice from '~/Slices/ProductNutritionalInformationSlice';
import * as SuppliersSlice from '~/Slices/SuppliersSlice';

export interface IFetchSlideoutDataParams {
	supplierCode: string;
	productNumber: string;
}

export interface IProductDetailsSlideoutState {
	isOpen: boolean;
	selectedProductKey: string;
}

const initialState: IProductDetailsSlideoutState = {
	isOpen: false,
	selectedProductKey: '',
};

export const fetchSlideoutData = createAsyncThunk(
	'detailsSlideout/fetchSlideoutData',
	async (
		params: IFetchSlideoutDataParams,
		{ dispatch, rejectWithValue, getState }
	) => {
		try {
			const state = getState() as IAppState;
			let supplier = undefined;
			const supplierResponse = await dispatch(
				SuppliersSlice.fetchSupplierByCode(params.supplierCode)
			);
			if (supplierResponse) {
				supplier = supplierResponse.payload;
			}
			const productKey = `${supplier.dssiCode}|${params.productNumber}`;
			const preloadedProductDetails =
				state.productDetails.details.entities[productKey];
			const preloadedNutritionalInfo =
				state.productNutrionalInformation.data.entities[productKey];
			const preloadedProductHeader = state.productHeaders.entities[productKey];
			const preloadedProductHeaderPrice =
				state.productHeaderPrices.entities[productKey];
			const locationLevelPricingEnabled =
				state.customerSettings.locationLevelPricingEnabled[supplier?.id];
			const codeAndNumberParams = {
				supplierCode: supplier.dssiCode,
				productNumber: params.productNumber,
			};
			const supplierIdAndNumberParams = {
				supplierId: supplier.id,
				productNumbers: [params.productNumber],
			};
			await Promise.all([
				dispatch(
					ProductDetailsSlideoutSlice.actions.setSelectedProductKey(productKey)
				),
				preloadedProductHeader ||
					dispatch(
						ProductHeadersSlice.fetchProductsByProductNumberAnyStatus(
							supplierIdAndNumberParams
						)
					),
				preloadedProductDetails ||
					dispatch(
						ProductDetailsSlice.fetchProductDetails(codeAndNumberParams)
					),
				preloadedNutritionalInfo ||
					dispatch(
						ProductNutritionalInformationSlice.fetchNutritionalInformation(
							codeAndNumberParams
						)
					),
				preloadedProductHeaderPrice ||
					dispatch(
						ProductHeaderPricesSlice.fetchHeaderLevelPrices([
							{
								corpSupplierCode: supplier.dssiCode,
								supplierProductNumber: params.productNumber,
							},
						])
					),
				locationLevelPricingEnabled != undefined ||
					dispatch(
						CustomerSettingsSlice.fetchLocationLevelPricingEnabled(supplier?.id)
					),
				dispatch(
					fetchBlueprintsAndRestrictionListsProductIsOn({
						supplierCode: supplier.dssiCode,
						productNumber: params.productNumber,
					})
				),
			]);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const requestLocationLevelPricingReport = createAsyncThunk<
	void,
	{ corpSupplierCode: string; productNumber: string; um: string },
	{ state: IAppState }
>(
	'detailsSlideout/requestLocationLevelPricingReport',
	async (args, { rejectWithValue }) => {
		try {
			const supplierResponse = await unwrapFetch2(
				Hub.SupplierControllerV3.GetSupplierByDssiCode(args.corpSupplierCode)
			);
			const supplierId = supplierResponse.id;

			const params: Hub.RequestLocationLevelPricingReportArgs = {
				supplierId: supplierId,
				supplierProductNumber: args.productNumber,
				unitOfMeasure: args.um,
			};
			const result = await unwrapFetch2(
				Hub.ReportController.RequestLocationLevelPricingReport(params)
			);
			if (result.errors.length > 0) {
				throw result.errors;
			}
			toastr.success({
				title: 'Report Started',
				body: `Report started for Location Level Pricing`,
			});
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const ProductDetailsSlideoutSlice = createSlice({
	name: 'detailsSlideout',
	initialState,
	reducers: {
		closeSlideout: (state) => {
			state.isOpen = false;
			state.selectedProductKey = '';
		},
		setSelectedProductKey: (state, action) => {
			state.selectedProductKey = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSlideoutData.pending, (state) => {
			state.isOpen = true;
		});
		builder.addCase(requestLocationLevelPricingReport.pending, (state) => {
			state.isOpen = false;
			state.selectedProductKey = '';
		});
	},
});

export const { closeSlideout, setSelectedProductKey } =
	ProductDetailsSlideoutSlice.actions;

export const slice = ProductDetailsSlideoutSlice;

export const thunks = {
	fetchSlideoutData,
	requestLocationLevelPricingReport,
};
